import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DndProvider, useDragLayer, useDrop, XYCoord } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import styled from '@emotion/styled';

import { useEditor, useEditorNode, useEditorNodeData } from '../../../../hooks';
import { DocumentPath } from '../../../../types';
import { setNode } from '../../../../utils';
import { ImageMapBrazeProps } from '../../ImageMapBraze/ImageMapBraze';
import { ImageMapShareProps } from '../../ImageMapShare/ImageMapShare';
import { ImageMapLinkProps } from '../ImageMapLink';
import { DraggableKnob, KNOB, KNOB_ID } from './DraggableKnob';
import { DraggableRectangle, RECTANGLE } from './DraggableRectangle';

export const ImageMapLinkEditor = React.memo(
  ({
    children,
    path,
  }: {
    children: React.ReactElement<ImageMapLinkProps | ImageMapShareProps | ImageMapBrazeProps>,
    path: DocumentPath,
  }) => {
    const { state, setState } = useEditor();
    const { node } = useEditorNodeData(path);
    const { isSelected, displayMode, onSelect } = useEditorNode(path);
    const [tempProps, setTempProps] = useState<ImageMapLinkProps | ImageMapShareProps | ImageMapBrazeProps>(
      children.props,
    );
    const [isDragging, setDragging] = useState<boolean>(false);

    if (displayMode !== 'edit') { return <>{ children }</>; }

    const container = useRef<HTMLDivElement>(null);
    const map = useRef<HTMLDivElement>(null);
    const [initialRelativeMapRect, setInitialRelativeRect] = useState<DOMRect | null>(null);

    const getRelativeMapRect = useDragHandler();

    const handleDragStart = useCallback(() => {
      if (!container.current || !map.current) { return; }
      const containerRect = container.current.getBoundingClientRect();
      const mapRect = map.current.getBoundingClientRect();
      const relativeRect = getRelativeRect(mapRect, containerRect);

      setInitialRelativeRect(relativeRect);
    }, [getRelativeRect]);

    const [, drop] = useDrop({
      accept: [RECTANGLE, KNOB],
      collect: (monitor) => ({ isOver: monitor.isOver() }),
      hover: (item, monitor) => {
        const delta = monitor.getDifferenceFromInitialOffset();

        if (!container.current || !map.current || !initialRelativeMapRect || !delta) { return; }

        // 1. get updated relative rect
        const relativeMapRect = getRelativeMapRect({
          type: monitor.getItemType(),
          item,
          cursorDiff: delta ?? {
            x: 0,
            y: 0,
          },
          initialRect: initialRelativeMapRect as DOMRect,
        });

        // 2. convert relative rect to style attributes
        const style = {
          top: `${getPercentage(relativeMapRect.top, container.current.offsetHeight)}%`,
          left: `${getPercentage(relativeMapRect.left, container.current.offsetWidth)}%`,
          right: `${getPercentage(
            relativeMapRect.right,
            container.current.offsetWidth,
          )}%`,
          bottom: `${getPercentage(
            relativeMapRect.bottom,
            container.current.offsetHeight,
          )}%`,
        };

        setTempProps((prevState) => ({ ...prevState, style }));
      },
    });
    drop(container);

    useEffect(() => {
      if (isDragging) { return; }

      function saveFinalPropsOnDocument() {
        setState({
          ...state,
          document: setNode(state.document, path, {
            ...node,
            props: {
              ...node.props,
              ...tempProps,
            },
          }),
        });
      }
      saveFinalPropsOnDocument();
    }, [isDragging]);

    return (
      <DndProvider backend={HTML5Backend}>
        <DroppableContainer
          ref={container}
          dragging={isDragging}
          onDrag={() => setDragging(true)}
          onDragStart={() => {
            handleDragStart();
            onSelect();
          }}
          onDragEnd={() => {
            setDragging(false);
            setInitialRelativeRect(null);
          }}
        >
          { React.cloneElement(
            children,
            tempProps,
            <DraggableRectangle ref={map}>
              { isSelected && (
                <>
                  <DraggableKnob id={KNOB_ID.TOP_LEFT} top left />
                  <DraggableKnob id={KNOB_ID.TOP_RIGHT} top right />
                  <DraggableKnob id={KNOB_ID.BOTTOM_LEFT} bottom left />
                  <DraggableKnob id={KNOB_ID.BOTTOM_RIGHT} bottom right />
                </>
              ) }
            </DraggableRectangle>,
          ) }
        </DroppableContainer>
      </DndProvider>
    );
  },
);

const DroppableContainer = styled.div<{
  dragging: boolean,
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: ${({ dragging }) => dragging ? 'initial' : 'none'};
  ${({ dragging }) => dragging && 'cursor: move;'}
`;

function useDragHandler() {
  function handleDrag({
    type,
    item,
    cursorDiff,
    initialRect,
  }: {
    type: symbol | string | null,
    item?: unknown,
    cursorDiff: XYCoord,
    initialRect: DOMRect,
  }): DOMRect {
    if (type === KNOB) {
      const knobId = (item as { id: KNOB_ID }).id;
      if (knobId === KNOB_ID.TOP_LEFT) {
        return {
          ...initialRect,
          top: initialRect.top + cursorDiff.y,
          left: initialRect.left + cursorDiff.x,
        };
      } else if (knobId === KNOB_ID.TOP_RIGHT) {
        return {
          ...initialRect,
          top: initialRect.top + cursorDiff.y,
          right: initialRect.right - cursorDiff.x,
        };
      } else if (knobId === KNOB_ID.BOTTOM_LEFT) {
        return {
          ...initialRect,
          left: initialRect.left + cursorDiff.x,
          bottom: initialRect.bottom - cursorDiff.y,
        };
      } else if (knobId === KNOB_ID.BOTTOM_RIGHT) {
        return {
          ...initialRect,
          right: initialRect.right - cursorDiff.x,
          bottom: initialRect.bottom - cursorDiff.y,
        };
      }
    } else if (type === RECTANGLE) {
      return {
        ...initialRect,
        top: initialRect.top + cursorDiff.y,
        left: initialRect.left + cursorDiff.x,
        right: initialRect.right - cursorDiff.x,
        bottom: initialRect.bottom - cursorDiff.y,
      };
    }

    return initialRect;
  }

  const [isDragging, setDragging] = useState(false);

  useDragLayer((monitor) => {
    const isCurrentlyDragging = monitor.isDragging();
    if (isDragging !== isCurrentlyDragging) {
      setDragging(isCurrentlyDragging);
    }
  });

  return handleDrag;
}

function getRelativeRect<T extends Partial<DOMRect>>(
  rect: T,
  parentRect: DOMRect,
): T {
  let result = {} as T;
  for (const key in rect) {
    if (key === 'top' || key === 'left') {
      result = {
        ...result,
        [key]:
          (rect[key] as unknown as number) -
          (parentRect[key as keyof DOMRect] as unknown as number),
      };
    } else if (key === 'right' || key === 'bottom') {
      result = {
        ...result,
        [key]:
          (parentRect[key as keyof DOMRect] as unknown as number) -
          (rect[key] as unknown as number),
      };
    }
  }
  return result;
}

function getPercentage(number: number, baseNumber: number) {
  return Math.min(Math.max((number / baseNumber) * 100, 0), 100);
}
